/**
 * Shared math primitives for hot-path game-logic code.
 *
 * Rationale: `Math.hypot(dx, dy)` is correct for magnitudes, but when the
 * caller only needs to compare against a threshold (range checks, proximity
 * tests) the sqrt is wasted work. `distSq` skips it and the caller compares
 * against `R * R` instead.
 */

/** Squared distance between (ax, ay) and (bx, by). */
export function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/**
 * Normalise an angle (radians) into `[-PI, PI]`.
 *
 * Non-finite inputs (NaN / ±Infinity) collapse to 0 — callers upstream
 * occasionally build angles from zero-length vectors via `atan2(0, 0)` or
 * divide through by a magnitude that has been clamped to 0, and letting
 * NaN propagate into the turret / body rotation state poisons every
 * subsequent frame until redeploy.
 */
export function wrapAngle(a: number): number {
  if (!isFinite(a)) return 0;
  a = a % (Math.PI * 2);
  if (a > Math.PI) a -= Math.PI * 2;
  if (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Shortest distance from point `(px, py)` to the finite line segment
 * `(x1, y1) → (x2, y2)`. Used by piercing / hitscan weapons to decide
 * which enemies a beam clips.
 *
 * Falls back to point-to-point distance when the segment is degenerate
 * (length² below a small epsilon) so callers don't have to branch.
 */
export function pointToLineDistance(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 0.001) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}
